import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, Plus, Clock, HandCoins, Receipt, Printer, Play } from "@phosphor-icons/react";
import { toast } from "sonner";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
const today = () => new Date().toISOString().slice(0, 10);
const thisMonth = () => new Date().toISOString().slice(0, 7);
const SALARY_TYPES = ["Monthly", "Daily", "Hourly"];

export default function HR() {
  const [dash, setDash] = useState(null);
  const [emps, setEmps] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [advances, setAdvances] = useState([]);
  const [payroll, setPayroll] = useState([]);
  const [tab, setTab] = useState("dashboard");
  const [empOpen, setEmpOpen] = useState(false);
  const [attOpen, setAttOpen] = useState(false);
  const [advOpen, setAdvOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [empForm, setEmpForm] = useState({ name: "", mobile: "", department: "Operations", designation: "", salary_type: "Monthly", basic_salary: "", date_of_joining: today(), bank_details: "", upi_id: "" });
  const [attForm, setAttForm] = useState({ employee_id: "", date: today(), check_in: "09:30", check_out: "18:00", break_hours: 1 });
  const [advForm, setAdvForm] = useState({ employee_id: "", date: today(), amount: "", reason: "" });
  const [payForm, setPayForm] = useState({ month: thisMonth(), ot_rate_per_hour: 150, incentive: 0, bonus: 0, allowances: 0, leave_deduction: 0, other_deduction: 0, deduct_advances: true });
  const [busy, setBusy] = useState(false);
  const [payrollMonth, setPayrollMonth] = useState(thisMonth());

  const load = useCallback(async () => {
    try {
      const [d, e, a, adv, p] = await Promise.all([
        api.get("/hr/dashboard"),
        api.get("/employees"),
        api.get("/attendance", { params: { date_from: today(), date_to: today() } }),
        api.get("/advances"),
        api.get("/payroll", { params: { month: payrollMonth } }),
      ]);
      setDash(d.data); setEmps(e.data); setAttendance(a.data); setAdvances(adv.data); setPayroll(p.data);
    } catch { }
  }, [payrollMonth]);
  useEffect(() => { load(); }, [load]);

  const saveEmp = async () => {
    if (!empForm.name || !empForm.mobile) { toast.error("Name and mobile required"); return; }
    setBusy(true);
    try {
      await api.post("/employees", { ...empForm, basic_salary: Number(empForm.basic_salary) || 0 });
      toast.success("Employee added"); setEmpOpen(false);
      setEmpForm({ name: "", mobile: "", department: "Operations", designation: "", salary_type: "Monthly", basic_salary: "", date_of_joining: today(), bank_details: "", upi_id: "" });
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const saveAtt = async () => {
    if (!attForm.employee_id) { toast.error("Pick employee"); return; }
    setBusy(true);
    try {
      await api.post("/attendance", { ...attForm, break_hours: Number(attForm.break_hours) || 0 });
      toast.success("Attendance marked"); setAttOpen(false); await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const saveAdv = async () => {
    if (!advForm.employee_id || !advForm.amount) { toast.error("Fill required fields"); return; }
    setBusy(true);
    try {
      await api.post("/advances", { ...advForm, amount: Number(advForm.amount) });
      toast.success("Advance recorded"); setAdvOpen(false);
      setAdvForm({ employee_id: "", date: today(), amount: "", reason: "" });
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const runPayroll = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payroll/run", payForm);
      toast.success(`Payroll generated for ${data.count} employees`);
      setPayOpen(false); setPayrollMonth(payForm.month); await load();
      setTab("payroll");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const printSlip = async (p) => {
    try {
      const { data } = await api.get(`/payroll/${p.id}/slip`);
      const w = window.open("", "_blank");
      if (!w) { toast.error("Popup blocked — please allow popups to view the payslip."); return; }
      w.document.write(slipHtml(data));
      w.document.close();
      w.focus();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="p-6 lg:p-8 max-w-[1600px] space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-violet-600 font-semibold mb-2 flex items-center gap-2">
            <Users size={14} weight="fill" /> Human Resources
          </div>
          <h1 className="text-3xl lg:text-4xl font-bold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>HR &amp; Payroll</h1>
          <p className="text-sm text-slate-600 mt-1.5">Employees, attendance, salary advances, monthly payroll runs and printable salary slips.</p>
        </div>
      </div>

      {/* HR KPIs */}
      {dash && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <KpiCard label="Total Staff" value={dash.total_staff} tone="violet" />
          <KpiCard label="Present Today" value={dash.present_today} tone="emerald" />
          <KpiCard label="Absent" value={dash.absent_today} tone="rose" />
          <KpiCard label="Today's Hours" value={dash.today_hours} tone="indigo" />
          <KpiCard label="Advance Outstanding" value={inr(dash.outstanding_advances)} tone="amber" />
          <KpiCard label="Salary Due (Month)" value={inr(dash.salary_due_month)} tone="cyan" />
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-100">
          <TabsTrigger value="dashboard" data-testid="hr-tab-dashboard">Overview</TabsTrigger>
          <TabsTrigger value="employees" data-testid="hr-tab-employees">Employees ({emps.length})</TabsTrigger>
          <TabsTrigger value="attendance" data-testid="hr-tab-attendance">Attendance</TabsTrigger>
          <TabsTrigger value="advances" data-testid="hr-tab-advances">Advances ({advances.length})</TabsTrigger>
          <TabsTrigger value="payroll" data-testid="hr-tab-payroll">Payroll ({payroll.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard">
          <Card className="p-4 bg-white text-sm text-slate-600">
            Use the tabs above to manage the team. Start by <b>adding employees</b>, then <b>mark attendance</b> daily, and <b>run payroll</b> at month-end to generate printable salary slips.
          </Card>
        </TabsContent>

        {/* Employees */}
        <TabsContent value="employees">
          <Card className="p-4 bg-white space-y-3">
            <div className="flex justify-between items-center">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Employees</div>
              <Button size="sm" onClick={() => setEmpOpen(true)} className="bg-violet-600 hover:bg-violet-700 text-white gap-1" data-testid="hr-new-emp"><Plus size={13} /> Add Employee</Button>
            </div>
            {emps.length === 0 ? <Empty text="No employees yet." /> : (
              <SimpleTable
                headers={["Emp #", "Name", "Mobile", "Department", "Designation", "Type", "Basic", "Status"]}
                rows={emps.map((e) => [
                  <span className="font-mono text-xs text-violet-700">{e.emp_no}</span>,
                  e.name, e.mobile, e.department, e.designation, e.salary_type, inr(e.basic_salary),
                  <Badge variant="outline" className={e.status === "Active" ? "border-emerald-300 text-emerald-700" : ""}>{e.status}</Badge>,
                ])}
              />
            )}
          </Card>
        </TabsContent>

        {/* Attendance */}
        <TabsContent value="attendance">
          <Card className="p-4 bg-white space-y-3">
            <div className="flex justify-between items-center">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Today's Attendance</div>
              <Button size="sm" onClick={() => setAttOpen(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white gap-1" data-testid="hr-new-att"><Clock size={13} /> Mark Attendance</Button>
            </div>
            {attendance.length === 0 ? <Empty text="Nothing marked yet today." /> : (
              <SimpleTable
                headers={["Emp", "Check In", "Check Out", "Break", "Hours", "OT", "Status"]}
                rows={attendance.map((a) => [
                  <span><b>{a.emp_no}</b> · {a.employee_name}</span>, a.check_in, a.check_out, a.break_hours, a.total_hours, a.overtime_hours,
                  <Badge variant="outline" className={a.status === "Present" ? "border-emerald-300 text-emerald-700" : a.status === "Half-day" ? "border-amber-300 text-amber-700" : "border-rose-300 text-rose-700"}>{a.status}</Badge>,
                ])}
              />
            )}
          </Card>
        </TabsContent>

        {/* Advances */}
        <TabsContent value="advances">
          <Card className="p-4 bg-white space-y-3">
            <div className="flex justify-between items-center">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Salary Advances</div>
              <Button size="sm" onClick={() => setAdvOpen(true)} className="bg-amber-600 hover:bg-amber-700 text-white gap-1" data-testid="hr-new-adv"><HandCoins size={13} /> New Advance</Button>
            </div>
            {advances.length === 0 ? <Empty text="No advances recorded yet." /> : (
              <SimpleTable
                headers={["Date", "Emp", "Amount", "Reason", "Approved By", "Status"]}
                rows={advances.map((a) => [
                  a.date, <span><b>{a.emp_no}</b> · {a.employee_name}</span>, inr(a.amount), a.reason || "—", a.approved_by,
                  <Badge variant="outline" className={a.status === "Settled" ? "border-emerald-300 text-emerald-700" : "border-amber-300 text-amber-700"}>{a.status}</Badge>,
                ])}
              />
            )}
          </Card>
        </TabsContent>

        {/* Payroll */}
        <TabsContent value="payroll">
          <Card className="p-4 bg-white space-y-3">
            <div className="flex justify-between items-center gap-2 flex-wrap">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Payroll — {payrollMonth}</div>
              <div className="flex items-center gap-2">
                <Input type="month" value={payrollMonth} onChange={(e) => setPayrollMonth(e.target.value)} className="h-9 w-40 text-sm" />
                <Button size="sm" onClick={() => setPayOpen(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1" data-testid="hr-run-payroll">
                  <Play size={13} weight="fill" /> Run Payroll
                </Button>
              </div>
            </div>
            {payroll.length === 0 ? <Empty text={`No payroll for ${payrollMonth} yet. Click 'Run Payroll' to generate.`} /> : (
              <SimpleTable
                headers={["Emp", "Basic", "OT", "Gross", "Advance", "Deductions", "Net", "Status", ""]}
                rows={payroll.map((p) => [
                  <span><b>{p.emp_no}</b> · {p.employee_name}</span>,
                  inr(p.basic_salary), inr(p.overtime_amount),
                  <b>{inr(p.gross)}</b>,
                  inr(p.advance_deduction),
                  inr(p.total_deductions),
                  <b className="text-emerald-700">{inr(p.net_salary)}</b>,
                  <Badge variant="outline">{p.status}</Badge>,
                  <Button size="sm" variant="outline" onClick={() => printSlip(p)} className="h-7 text-[10px] gap-1" data-testid={`hr-slip-${p.emp_no}`}><Printer size={11} weight="bold" /> Slip</Button>,
                ])}
              />
            )}
          </Card>
        </TabsContent>
      </Tabs>

      {/* Employee dialog */}
      <Dialog open={empOpen} onOpenChange={setEmpOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader><DialogTitle>New Employee</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Name *"><Input value={empForm.name} onChange={(e) => setEmpForm({ ...empForm, name: e.target.value })} data-testid="hr-emp-name" /></Field>
            <Field label="Mobile *"><Input value={empForm.mobile} onChange={(e) => setEmpForm({ ...empForm, mobile: e.target.value })} data-testid="hr-emp-mobile" /></Field>
            <Field label="Department"><Input value={empForm.department} onChange={(e) => setEmpForm({ ...empForm, department: e.target.value })} /></Field>
            <Field label="Designation"><Input value={empForm.designation} onChange={(e) => setEmpForm({ ...empForm, designation: e.target.value })} /></Field>
            <Field label="Salary Type">
              <Select value={empForm.salary_type} onValueChange={(v) => setEmpForm({ ...empForm, salary_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{SALARY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Basic Salary"><Input type="number" value={empForm.basic_salary} onChange={(e) => setEmpForm({ ...empForm, basic_salary: e.target.value })} data-testid="hr-emp-basic" /></Field>
            <Field label="Date of Joining" full><Input type="date" value={empForm.date_of_joining} onChange={(e) => setEmpForm({ ...empForm, date_of_joining: e.target.value })} /></Field>
            <Field label="Bank Details" full><Input value={empForm.bank_details} onChange={(e) => setEmpForm({ ...empForm, bank_details: e.target.value })} placeholder="Bank · A/C · IFSC" /></Field>
            <Field label="UPI ID" full><Input value={empForm.upi_id} onChange={(e) => setEmpForm({ ...empForm, upi_id: e.target.value })} placeholder="9876543210@upi" /></Field>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setEmpOpen(false)}>Cancel</Button><Button onClick={saveEmp} disabled={busy} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="hr-emp-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Attendance dialog */}
      <Dialog open={attOpen} onOpenChange={setAttOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader><DialogTitle>Mark Attendance</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Employee" full>
              <Select value={attForm.employee_id} onValueChange={(v) => setAttForm({ ...attForm, employee_id: v })}>
                <SelectTrigger data-testid="hr-att-emp"><SelectValue placeholder="Pick employee" /></SelectTrigger>
                <SelectContent>{emps.map((e) => <SelectItem key={e.id} value={e.id}>{e.emp_no} · {e.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Date"><Input type="date" value={attForm.date} onChange={(e) => setAttForm({ ...attForm, date: e.target.value })} /></Field>
            <Field label="Break (hrs)"><Input type="number" step="0.25" value={attForm.break_hours} onChange={(e) => setAttForm({ ...attForm, break_hours: e.target.value })} /></Field>
            <Field label="Check In"><Input type="time" value={attForm.check_in} onChange={(e) => setAttForm({ ...attForm, check_in: e.target.value })} /></Field>
            <Field label="Check Out"><Input type="time" value={attForm.check_out} onChange={(e) => setAttForm({ ...attForm, check_out: e.target.value })} /></Field>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setAttOpen(false)}>Cancel</Button><Button onClick={saveAtt} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="hr-att-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Advance dialog */}
      <Dialog open={advOpen} onOpenChange={setAdvOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader><DialogTitle>Salary Advance</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Employee" full>
              <Select value={advForm.employee_id} onValueChange={(v) => setAdvForm({ ...advForm, employee_id: v })}>
                <SelectTrigger data-testid="hr-adv-emp"><SelectValue placeholder="Pick employee" /></SelectTrigger>
                <SelectContent>{emps.map((e) => <SelectItem key={e.id} value={e.id}>{e.emp_no} · {e.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Date"><Input type="date" value={advForm.date} onChange={(e) => setAdvForm({ ...advForm, date: e.target.value })} /></Field>
            <Field label="Amount"><Input type="number" value={advForm.amount} onChange={(e) => setAdvForm({ ...advForm, amount: e.target.value })} data-testid="hr-adv-amt" /></Field>
            <Field label="Reason" full><Textarea rows={2} value={advForm.reason} onChange={(e) => setAdvForm({ ...advForm, reason: e.target.value })} /></Field>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setAdvOpen(false)}>Cancel</Button><Button onClick={saveAdv} disabled={busy} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="hr-adv-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payroll run dialog */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader><DialogTitle>Run Payroll</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Month *"><Input type="month" value={payForm.month} onChange={(e) => setPayForm({ ...payForm, month: e.target.value })} data-testid="hr-pay-month" /></Field>
            <Field label="OT Rate (₹/hr)"><Input type="number" value={payForm.ot_rate_per_hour} onChange={(e) => setPayForm({ ...payForm, ot_rate_per_hour: Number(e.target.value) })} /></Field>
            <Field label="Incentive"><Input type="number" value={payForm.incentive} onChange={(e) => setPayForm({ ...payForm, incentive: Number(e.target.value) })} /></Field>
            <Field label="Bonus"><Input type="number" value={payForm.bonus} onChange={(e) => setPayForm({ ...payForm, bonus: Number(e.target.value) })} /></Field>
            <Field label="Allowances"><Input type="number" value={payForm.allowances} onChange={(e) => setPayForm({ ...payForm, allowances: Number(e.target.value) })} /></Field>
            <Field label="Leave Deduction"><Input type="number" value={payForm.leave_deduction} onChange={(e) => setPayForm({ ...payForm, leave_deduction: Number(e.target.value) })} /></Field>
            <div className="col-span-2 flex items-center gap-2 text-sm border-t border-slate-100 pt-2">
              <input type="checkbox" id="ded-adv" checked={payForm.deduct_advances} onChange={(e) => setPayForm({ ...payForm, deduct_advances: e.target.checked })} />
              <label htmlFor="ded-adv">Auto-deduct outstanding salary advances</label>
            </div>
          </div>
          <div className="rounded bg-emerald-50 border border-emerald-200 p-2 text-xs text-emerald-800">
            Gross = Basic + OT (hours × rate) + Incentive + Bonus + Allowances.<br/>Net = Gross − Advances − Deductions.
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setPayOpen(false)}>Cancel</Button><Button onClick={runPayroll} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1" data-testid="hr-pay-run"><Play size={13} weight="fill" /> {busy ? "Running…" : "Run"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function slipHtml(d) {
  const p = d.payroll || {};
  const b = d.branch || {};
  const e = d.employee || {};
  const monthLabel = formatMonth(p.month);
  const initials = (b.name || "PE").split(/\s+/).map(w => w[0]).slice(0, 2).join("").toUpperCase();
  const netWords = amountInWords(Math.round(Number(p.net_salary || 0)));
  const genDate = p.generated_at ? new Date(p.generated_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "";

  return `<!doctype html><html><head><meta charset="utf-8"/><title>Salary Slip · ${p.emp_no || ""} · ${monthLabel}</title>
<style>
  *{box-sizing:border-box}
  body{font-family:'Segoe UI',-apple-system,BlinkMacSystemFont,Roboto,sans-serif;margin:0;padding:24px;background:#f1f5f9;color:#0f172a;-webkit-print-color-adjust:exact;print-color-adjust:exact}
  .sheet{max-width:820px;margin:0 auto;background:#fff;border:1px solid #e2e8f0;border-radius:12px;overflow:hidden;box-shadow:0 4px 24px rgba(15,23,42,.06)}
  .hdr{display:flex;align-items:center;justify-content:space-between;padding:22px 28px;background:linear-gradient(135deg,#4c1d95 0%,#6d28d9 55%,#7c3aed 100%);color:#fff}
  .brand{display:flex;align-items:center;gap:14px}
  .logo{width:56px;height:56px;border-radius:12px;background:#fff;color:#4c1d95;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:22px;letter-spacing:.5px;box-shadow:0 4px 10px rgba(0,0,0,.15)}
  .co-name{font-size:20px;font-weight:800;letter-spacing:.2px;margin:0}
  .co-meta{font-size:11px;opacity:.85;margin-top:2px;line-height:1.5}
  .doc-label{text-align:right}
  .doc-label .tag{display:inline-block;padding:3px 10px;background:rgba(255,255,255,.18);border:1px solid rgba(255,255,255,.35);border-radius:999px;font-size:10px;letter-spacing:.18em;text-transform:uppercase;font-weight:600}
  .doc-label h2{margin:6px 0 0;font-size:18px;font-weight:700}
  .doc-label .period{font-size:11px;opacity:.9;margin-top:2px}

  .band{display:grid;grid-template-columns:repeat(4,1fr);gap:0;padding:14px 28px;background:#faf5ff;border-bottom:1px solid #ede9fe}
  .band .cell{border-right:1px dashed #ddd6fe;padding:2px 12px 2px 0}
  .band .cell:last-child{border-right:none}
  .band .k{font-size:9px;text-transform:uppercase;letter-spacing:.14em;color:#7c3aed;font-weight:700}
  .band .v{font-size:13px;font-weight:600;color:#1e293b;margin-top:3px;word-break:break-word}

  .section{padding:20px 28px}
  .section-title{font-size:10px;letter-spacing:.18em;text-transform:uppercase;color:#64748b;font-weight:700;margin-bottom:10px;border-bottom:1px solid #e2e8f0;padding-bottom:6px}
  .grid2{display:grid;grid-template-columns:1fr 1fr;gap:0 24px;font-size:12.5px}
  .row{display:flex;justify-content:space-between;padding:7px 0;border-bottom:1px dotted #e2e8f0}
  .row .lbl{color:#64748b}
  .row .val{font-weight:600;color:#0f172a;text-align:right}
  .row.total{border-top:2px solid #0f172a;border-bottom:2px solid #0f172a;padding:9px 0;font-weight:800}
  .row.total .lbl{color:#0f172a}
  .row.total .val{color:#0f172a}

  .earnings .row.total{background:#ecfdf5;padding:9px 8px;border:2px solid #10b981}
  .deductions .row.total{background:#fef2f2;padding:9px 8px;border:2px solid #ef4444}

  .net-wrap{margin:22px 28px 0;background:linear-gradient(135deg,#059669 0%,#10b981 100%);color:#fff;border-radius:10px;padding:18px 22px;display:flex;justify-content:space-between;align-items:center;gap:12px}
  .net-wrap .k{font-size:11px;text-transform:uppercase;letter-spacing:.2em;opacity:.85}
  .net-wrap .amt{font-size:26px;font-weight:800;margin-top:2px}
  .net-wrap .words{font-size:11px;font-style:italic;opacity:.92;margin-top:4px;max-width:60%;text-align:right}

  .sig{display:flex;justify-content:space-between;padding:36px 28px 12px;font-size:11px;color:#64748b}
  .sig .box{width:40%;text-align:center}
  .sig .line{border-top:1px solid #94a3b8;margin-bottom:6px;padding-top:38px}

  .footer{padding:10px 28px 18px;font-size:10px;color:#94a3b8;text-align:center;border-top:1px dashed #e2e8f0}
  .no-print{position:fixed;top:14px;right:18px;display:flex;gap:8px}
  .btn{background:#4c1d95;color:#fff;border:none;padding:9px 16px;border-radius:8px;font-weight:600;cursor:pointer;font-size:13px;box-shadow:0 4px 10px rgba(76,29,149,.35)}
  .btn.ghost{background:#fff;color:#4c1d95;border:1px solid #4c1d95}
  @media print{
    body{background:#fff;padding:0}
    .no-print{display:none}
    .sheet{border:none;box-shadow:none;border-radius:0;max-width:100%}
  }
</style></head><body>
  <div class="no-print">
    <button class="btn ghost" onclick="window.close()">Close</button>
    <button class="btn" onclick="window.print()">Print / Save PDF</button>
  </div>
  <div class="sheet">
    <div class="hdr">
      <div class="brand">
        <div class="logo">${escapeHtml(initials)}</div>
        <div>
          <p class="co-name">${escapeHtml(b.name || "Priyanka Enterprise")}</p>
          <div class="co-meta">
            ${escapeHtml([b.address, b.city].filter(Boolean).join(", ") || "Common Service Centre · Multi-branch ERP")}<br/>
            ${b.contact ? "📞 " + escapeHtml(b.contact) : ""}${b.code ? ` &nbsp;·&nbsp; Branch: <b>${escapeHtml(b.code)}</b>` : ""}
          </div>
        </div>
      </div>
      <div class="doc-label">
        <span class="tag">Salary Slip</span>
        <h2>${escapeHtml(monthLabel)}</h2>
        <div class="period">Pay Period · ${escapeHtml(p.month || "")}</div>
      </div>
    </div>

    <div class="band">
      <div class="cell"><div class="k">Employee ID</div><div class="v">${escapeHtml(e.emp_no || p.emp_no || "—")}</div></div>
      <div class="cell"><div class="k">Employee Name</div><div class="v">${escapeHtml(e.name || p.employee_name || "—")}</div></div>
      <div class="cell"><div class="k">Designation</div><div class="v">${escapeHtml(e.designation || p.designation || "—")}</div></div>
      <div class="cell"><div class="k">Department</div><div class="v">${escapeHtml(e.department || "—")}</div></div>
    </div>

    <div class="section">
      <div class="section-title">Employee Summary</div>
      <div class="grid2">
        <div>
          <div class="row"><span class="lbl">Date of Joining</span><span class="val">${escapeHtml(e.date_of_joining || "—")}</span></div>
          <div class="row"><span class="lbl">Mobile</span><span class="val">${escapeHtml(e.mobile || "—")}</span></div>
          <div class="row"><span class="lbl">Salary Type</span><span class="val">${escapeHtml(e.salary_type || "Monthly")}</span></div>
        </div>
        <div>
          <div class="row"><span class="lbl">Working Days</span><span class="val">${p.working_days ?? "—"}</span></div>
          <div class="row"><span class="lbl">Present · Half · Absent</span><span class="val">${p.present ?? 0} · ${p.half_day ?? 0} · ${p.absent ?? 0}</span></div>
          <div class="row"><span class="lbl">Total Hours (incl. OT)</span><span class="val">${p.total_hours ?? 0} hrs (OT ${p.overtime_hours ?? 0} hrs)</span></div>
        </div>
      </div>
    </div>

    <div class="section" style="padding-top:0">
      <div class="grid2">
        <div class="earnings">
          <div class="section-title" style="color:#059669;border-color:#a7f3d0">Earnings</div>
          <div class="row"><span class="lbl">Basic Salary</span><span class="val">₹ ${fmt(p.basic_salary)}</span></div>
          <div class="row"><span class="lbl">Overtime (${p.overtime_hours || 0} hrs)</span><span class="val">₹ ${fmt(p.overtime_amount)}</span></div>
          <div class="row"><span class="lbl">Incentive</span><span class="val">₹ ${fmt(p.incentive)}</span></div>
          <div class="row"><span class="lbl">Bonus</span><span class="val">₹ ${fmt(p.bonus)}</span></div>
          <div class="row"><span class="lbl">Allowances</span><span class="val">₹ ${fmt(p.allowances)}</span></div>
          <div class="row total"><span class="lbl">Gross Earnings</span><span class="val">₹ ${fmt(p.gross)}</span></div>
        </div>
        <div class="deductions">
          <div class="section-title" style="color:#dc2626;border-color:#fecaca">Deductions</div>
          <div class="row"><span class="lbl">Advance Deduction</span><span class="val">₹ ${fmt(p.advance_deduction)}</span></div>
          <div class="row"><span class="lbl">Leave Deduction</span><span class="val">₹ ${fmt(p.leave_deduction)}</span></div>
          <div class="row"><span class="lbl">Other Deduction</span><span class="val">₹ ${fmt(p.other_deduction)}</span></div>
          <div class="row"><span class="lbl">&nbsp;</span><span class="val">&nbsp;</span></div>
          <div class="row"><span class="lbl">&nbsp;</span><span class="val">&nbsp;</span></div>
          <div class="row total"><span class="lbl">Total Deductions</span><span class="val">₹ ${fmt(p.total_deductions)}</span></div>
        </div>
      </div>
    </div>

    <div class="net-wrap">
      <div>
        <div class="k">Net Salary Payable</div>
        <div class="amt">₹ ${fmt(p.net_salary)}</div>
      </div>
      <div class="words">${escapeHtml(netWords)}</div>
    </div>

    ${(e.bank_details || e.upi_id) ? `
    <div class="section" style="padding-top:22px">
      <div class="section-title">Payment Details</div>
      <div class="grid2">
        <div class="row"><span class="lbl">Bank / A/C / IFSC</span><span class="val">${escapeHtml(e.bank_details || "—")}</span></div>
        <div class="row"><span class="lbl">UPI ID</span><span class="val">${escapeHtml(e.upi_id || "—")}</span></div>
      </div>
    </div>` : ""}

    <div class="footer" style="margin-top:32px">
      This is a system-generated payslip and does not require a physical signature.<br/>
      Generated on ${escapeHtml(genDate)} · by ${escapeHtml(p.generated_by || "system")}
    </div>
  </div>
</body></html>`;
}

function fmt(n) { return Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 }); }

function escapeHtml(s) {
  return String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}

function formatMonth(ym) {
  if (!ym || !/^\d{4}-\d{2}$/.test(ym)) return ym || "";
  const [y, m] = ym.split("-");
  const names = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  return `${names[Number(m) - 1]} ${y}`;
}

// Indian-numbering amount-in-words (₹, no paise for slip clarity)
function amountInWords(num) {
  if (!Number.isFinite(num) || num <= 0) return "Rupees Zero Only";
  const a = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten",
    "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
  const b = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  const two = (n) => n < 20 ? a[n] : b[Math.floor(n / 10)] + (n % 10 ? " " + a[n % 10] : "");
  const three = (n) => (n >= 100 ? a[Math.floor(n / 100)] + " Hundred" + (n % 100 ? " " + two(n % 100) : "") : two(n));
  let n = Math.floor(num);
  const crore = Math.floor(n / 10000000); n %= 10000000;
  const lakh = Math.floor(n / 100000); n %= 100000;
  const thousand = Math.floor(n / 1000); n %= 1000;
  const hundred = n;
  const parts = [];
  if (crore) parts.push(three(crore) + " Crore");
  if (lakh) parts.push(two(lakh) + " Lakh");
  if (thousand) parts.push(two(thousand) + " Thousand");
  if (hundred) parts.push(three(hundred));
  return "Rupees " + parts.join(" ").trim() + " Only";
}

function KpiCard({ label, value, tone }) {
  const t = { violet: "border-l-violet-500", emerald: "border-l-emerald-500", rose: "border-l-rose-500", indigo: "border-l-indigo-500", amber: "border-l-amber-500", cyan: "border-l-cyan-500" };
  return (
    <Card className={`p-3 border-l-4 ${t[tone] || t.indigo} bg-white`}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">{label}</div>
      <div className="text-xl font-bold text-slate-900 mt-0.5" style={{ fontFamily: "var(--font-heading)" }}>{value}</div>
    </Card>
  );
}
function Field({ label, children, full }) { return <div className={full ? "col-span-2" : ""}><div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 font-semibold">{label}</div>{children}</div>; }
function Empty({ text }) { return <div className="text-center py-8 text-sm text-slate-400">{text}</div>; }
function SimpleTable({ headers, rows }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold border-b border-slate-200">
          <tr>{headers.map((h, i) => <th key={i} className="text-left py-2 pr-3">{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
              {r.map((c, j) => <td key={j} className="py-2 pr-3 text-slate-700">{c ?? "—"}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
